import {
  CSV_MAX_FILE_SIZE,
  CSV_MAX_FILE_SIZE_MB,
  DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_RESPONSE_CODE,
  DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_TRANSLATE_KEY,
  DEVICE_EXISTS_DIFFERENT_TENANT_RESPONSE_CODE,
  DEVICE_EXISTS_DIFFERENT_TENANT_TRANSLATE_KEY,
  DEVICE_EXISTS_SAME_TENANT_RESPONSE_CODE,
  DEVICE_EXISTS_SAME_TENANT_TRANSLATE_KEY,
  DEVICE_SETTING_INCORRECT,
  DOCS,
  FAILED_TO_SET_SITE,
  FAILED_TO_SET_SITE_CONFIGURATION,
  FAILED_TO_SET_SITE_TOPOLOGY,
  FILE_EXTENSIONS,
  INVALID_CIDR_VALUE,
  NO_MODELS_PROVIDED,
  ZIP_SUPPORTED_FORMATS
} from "Constants";
import { HelpLink } from "elements";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import {
  DeviceImportResponse,
  GlobalConfigResponse,
  GroupConfigResponse,
  Maybe,
  ModelConfigSettingsResponse,
  SiteConfigResponse,
  SiteTopologyResponse,
  useDeviceCountForTenantQuery
} from "generated/graphql";
import { convertToBase64, createDownloadableFile } from "utilities/fileUtils";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { array, boolean, mixed, object } from "yup";
import { Import } from "./Import";

export type DeviceErrors = {
  deviceId: string;
  deviceModel: string;
  errors: string[];
};
export type SettingsParam = {
  deviceId: string;
  deviceModel: string;
  settingsParam: string[];
};

export const migrationFileSchema = object({
  files: array().of(
    mixed()
      .test(
        "fileSize",
        i18n.t("FILE_SIZE_ERROR_DESC", { size: CSV_MAX_FILE_SIZE_MB }),
        value => value.size < CSV_MAX_FILE_SIZE
      )
      .test(
        "fileFormat",
        i18n.t("FILE_FORMAT_ERROR"),
        value =>
          ZIP_SUPPORTED_FORMATS.includes(value.type) &&
          value.name.split(".").pop() === "zip"
      )
  ),
  updateExistingDevices: boolean(),
  overwriteDeviceRecords: boolean()
});

export const useImportResponseHandler = () => {
  const { t } = useTranslation();

  const getDeviceImportResponse = (
    deviceResponse: Maybe<DeviceImportResponse>[]
  ): { deviceErrors: DeviceErrors[]; configParam: SettingsParam[] } => {
    // array of codes we need to look for/handle
    const errorCodes = [
      {
        lookup: DEVICE_EXISTS_SAME_TENANT_RESPONSE_CODE,
        errorCode: DEVICE_EXISTS_SAME_TENANT_TRANSLATE_KEY
      },
      {
        lookup: DEVICE_EXISTS_DIFFERENT_TENANT_RESPONSE_CODE,
        errorCode: DEVICE_EXISTS_DIFFERENT_TENANT_TRANSLATE_KEY
      },
      {
        lookup: DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_RESPONSE_CODE,
        errorCode: DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_TRANSLATE_KEY
      }
    ];

    const failedDevices = deviceResponse
      ?.map(r => ({
        deviceId: r?.deviceId,
        deviceModel: r?.deviceModel,
        errors: r?.deviceReport?.reduce((errors, s) => {
          for (const error of errorCodes) {
            if (s?.includes(error.lookup)) {
              errors.push(error.errorCode);
              break;
            }
          }

          return errors;
        }, [] as string[])
      }))
      .filter(r => r.errors?.length) as DeviceErrors[];

    const failedConfigParams = deviceResponse
      ?.map(r => ({
        deviceId: r?.deviceId,
        deviceModel: r?.deviceModel,
        settingsParam: r?.deviceReport?.reduce((failedParams, s) => {
          if (s?.includes(DEVICE_SETTING_INCORRECT)) {
            failedParams.push(s);
          }

          return failedParams;
        }, [] as string[])
      }))
      .filter(r => r.settingsParam?.length) as SettingsParam[];

    return {
      deviceErrors: failedDevices,
      configParam: failedConfigParams
    };
  };

  // Create error text for an individual device import error
  const createDeviceErrorText = (error: Maybe<string>) => {
    let errorText = "";
    if (
      error === DEVICE_EXISTS_SAME_TENANT_TRANSLATE_KEY ||
      error === DEVICE_EXISTS_DIFFERENT_TENANT_TRANSLATE_KEY ||
      error === DEVICE_COULD_NOT_BE_ASSIGNED_TO_SITE_TRANSLATE_KEY
    ) {
      errorText = t(error);
    } else if (error) {
      errorText = error
        .replace("INCORRECT SETTING NAME", "")
        .replace("INCORRECT DATATYPE", "");
    }

    return errorText;
  };

  const createDeviceImportError = (
    failedDevices: DeviceErrors[],
    failedConfigParams: SettingsParam[]
  ): { deviceErrors: string; configParam: string } => {
    const deviceErrors = failedDevices
      .map(
        device =>
          `${t("DEVICE_ID")}: ${device.deviceId}\n\t${t("MODEL")}: ${
            device.deviceModel
          }\n\t${t("ERROR")}: ${device.errors
            ?.map(createDeviceErrorText)
            .join("\n\t")}`
      )
      .join("\n\n\t");

    const configErrors = failedConfigParams
      .map(
        configParam =>
          `${t("DEVICE_ID")}: ${configParam.deviceId}\n\t${t("MODEL")}: ${
            configParam.deviceModel
          }\n\t${t(
            "SETTINGS_PARAMETER"
          )}:\n\t\t${configParam.settingsParam
            ?.map(createDeviceErrorText)
            .join("\n\t\t")}`
      )
      .join("\n\n\t");

    return {
      deviceErrors: deviceErrors
        ? `${t("DEVICE_CONFIG")}:\n\t${deviceErrors}`
        : "",
      configParam: configErrors
        ? `${t("DEVICE_CONFIG")}:\n\t${configErrors}`
        : ""
    };
  };

  const parseSiteTopologyError = (error: Maybe<string>) => {
    let text = error;
    // check for general site import error
    if (error?.includes(FAILED_TO_SET_SITE_TOPOLOGY)) {
      text = t("SITE_IMPORT_FAILED");
    } else {
      // check for invalid CIDR
      const index = error?.indexOf(INVALID_CIDR_VALUE);
      if (index && index >= 0) {
        const newError = error?.slice(index);
        text = newError
          ? newError[0].toUpperCase() + newError.substring(1)
          : "";
      }
    }

    return text;
  };

  const createSiteTopologyErrorText = (
    responses: Maybe<SiteTopologyResponse>[]
  ) => {
    const errorText = responses
      .map(
        response =>
          `${response?.siteName}:\n\t\t${response?.siteTopologyErrors
            ?.map(parseSiteTopologyError)
            .join("\n\t\t")}`
      )
      .join("\n\n\t");

    return `${t("SITE_TOPOLOGY")}:\n\t${errorText}`;
  };

  const createSiteConfigErrorText = (
    entity: LocalKeys,
    title: LocalKeys,
    configErrors: SiteConfigResponse[]
  ) => {
    let errors = "";
    const importErrors: SiteConfigResponse[] = [];
    configErrors.forEach(res => {
      if (res.siteConfigError) {
        importErrors.push(res);
      }
    });
    if (importErrors.length > 0) {
      const importErrorsByEntity = importErrors.map(
        ({ siteName: name, siteConfigError: error }) => {
          let errorMessage = error;
          if (errorMessage?.startsWith(FAILED_TO_SET_SITE_CONFIGURATION)) {
            const errorCause = errorMessage
              .substring(FAILED_TO_SET_SITE_CONFIGURATION.length + 1)
              .trim();
            errorMessage = t("SITE_IMPORT_POLICY_FAILED");
            if (errorCause === NO_MODELS_PROVIDED) {
              errorMessage += `: ${t("NO_MODELS_PROVIDED")}`;
            }
          } else if (errorMessage?.startsWith(FAILED_TO_SET_SITE)) {
            errorMessage = t("SITE_IMPORT_FAILED");
          }

          return `${t(entity)}: ${name}\n\t${t("ERROR")}: ${errorMessage}`;
        }
      );

      errors = `${t(title)}:\n\t${importErrorsByEntity.join("\n\n\t")}`;
    }

    const configParamErrors: SiteConfigResponse[] = [];

    configErrors.forEach(res => {
      if (res.siteConfigSettings && res.siteConfigSettings.length) {
        configParamErrors.push(res);
      }
    });
    let configParam = "";
    if (configParamErrors.length > 0) {
      const arrayOfConfigErrors: string[] = [];
      configParamErrors.forEach(
        ({ siteName: name, siteConfigSettings: configParam }) => {
          const errorsByDeviceModel: string[] = [];
          if (configParam) {
            configParam.forEach(param => {
              if (param && param.configAttributes) {
                errorsByDeviceModel.push(
                  `${t("MODEL")}: ${param.deviceModel}\n\t\t${t(
                    "SETTINGS_PARAMETER"
                  )}:\n\t\t\t${param.configAttributes
                    ?.map(createDeviceErrorText)
                    .join("\n\t\t\t")}`
                );
              }
            });
          }
          arrayOfConfigErrors.push(
            `${t(entity)}: ${name}\n\t\t${errorsByDeviceModel.join("\n\n\t\t")}`
          );
        }
      );
      const configParamsByEntity = arrayOfConfigErrors.join("\n\n\t");

      configParam = `${t(title)}:\n\t${configParamsByEntity}`;
    }

    return {
      errors,
      configParam
    };
  };

  const createGroupConfigErrorText = (
    entity: LocalKeys,
    title: LocalKeys,
    configErrors: GroupConfigResponse[]
  ) => {
    const configParamErrors: GroupConfigResponse[] = [];

    configErrors.forEach(res => {
      if (res.groupConfigSettings && res.groupConfigSettings.length) {
        configParamErrors.push(res);
      }
    });
    let configParam = "";
    if (configParamErrors.length > 0) {
      const arrayOfConfigErrors: string[] = [];
      configParamErrors.forEach(
        ({ groupName: name, groupConfigSettings: configParam }) => {
          const errorsByDeviceModel: string[] = [];
          if (configParam) {
            configParam.forEach(param => {
              if (param) {
                errorsByDeviceModel.push(
                  `${t("MODEL")}: ${param.deviceModel}\n\t\t${t(
                    "SETTINGS_PARAMETER"
                  )}:\n\t\t\t${param.configAttributes
                    ?.map(createDeviceErrorText)
                    .join("\n\t\t\t")}`
                );
              }
            });
          }
          arrayOfConfigErrors.push(
            `${t(entity)}: ${name}\n\t\t${errorsByDeviceModel.join("\n\n\t\t")}`
          );
        }
      );
      const configParamsByEntity = arrayOfConfigErrors.join("\n\n\t");

      configParam = `${t(title)}:\n\t${configParamsByEntity}`;
    }

    return {
      errors: "",
      configParam
    };
  };

  const createDeviceModelConfigErrorText = (
    globalConfigErrors: GlobalConfigResponse[]
  ) => {
    const errorsByDeviceModel = globalConfigErrors
      .map(
        ({ deviceModel, globalConfigSettings: errors }) =>
          `${t("MODEL")}: ${deviceModel}\n\t${t(
            "SETTINGS_PARAMETER"
          )}:\n\t\t${errors?.map(createDeviceErrorText).join("\n\t\t")}`
      )
      .join("\n\n\t");

    return `${t("DEVICE_MODEL_CONFIG")}:\n\t${errorsByDeviceModel}`;
  };

  const handleDeviceImportResponse = (
    response: Maybe<DeviceImportResponse>[]
  ): { deviceErrors: string; configParam: string } => {
    const deviceImportResponse = getDeviceImportResponse(response);
    return createDeviceImportError(
      deviceImportResponse.deviceErrors,
      deviceImportResponse.configParam
    );
  };

  const handleSiteTopologyResponse = (
    response: Maybe<SiteTopologyResponse>[]
  ) => {
    const siteTopologyErrors = response.filter(
      site => site?.siteTopologyErrors
    );
    return siteTopologyErrors.length
      ? createSiteTopologyErrorText(siteTopologyErrors)
      : "";
  };

  const getConfigParamErrors = (
    settingsResponse: Maybe<Maybe<ModelConfigSettingsResponse>[]>
  ) =>
    settingsResponse
      ?.map(result => ({
        __typename: "ModelConfigSettingsResponse" as const,
        deviceModel: handleNullFields(result?.deviceModel),
        configAttributes:
          result?.configAttributes?.filter(
            (attr): attr is string =>
              !!attr && attr.includes(DEVICE_SETTING_INCORRECT)
          ) ?? []
      }))
      .filter(({ configAttributes }) => configAttributes.length > 0) ?? [];

  const handleSiteConfigResponse = (
    response: Maybe<SiteConfigResponse>[]
  ): { errors: string; configParam: string } => {
    const entity: LocalKeys = "SITE";
    const title: LocalKeys = "SITE_CONFIG";

    const errors: SiteConfigResponse[] = [];
    response.forEach(result => {
      if (result) {
        const configParamErrors = getConfigParamErrors(
          result.siteConfigSettings
        );
        errors.push({ ...result, siteConfigSettings: configParamErrors });
      }
    });

    return createSiteConfigErrorText(entity, title, errors);
  };

  const handleGroupConfigResponse = (
    response: Maybe<GroupConfigResponse>[]
  ): { errors: string; configParam: string } => {
    const entity: LocalKeys = "GROUP";
    const title: LocalKeys = "GROUP_CONFIG";

    const errors: GroupConfigResponse[] = [];

    response.forEach(result => {
      if (result) {
        const configParamErrors = getConfigParamErrors(
          result.groupConfigSettings
        );
        errors.push({ ...result, groupConfigSettings: configParamErrors });
      }
    });

    return createGroupConfigErrorText(entity, title, errors);
  };

  const handleGlobalConfigResponse = (
    response: Maybe<GlobalConfigResponse>[]
  ) => {
    const globalConfigErrors: GlobalConfigResponse[] = [];
    response.forEach(r => {
      if (r) {
        globalConfigErrors.push({
          __typename: "GlobalConfigResponse",
          deviceModel: handleNullFields(r.deviceModel),
          globalConfigSettings:
            r.globalConfigSettings?.filter(
              s => !!s && s.includes(DEVICE_SETTING_INCORRECT)
            ) ?? []
        });
      }
    });

    return globalConfigErrors.length
      ? createDeviceModelConfigErrorText(
          globalConfigErrors.filter(
            r => r.globalConfigSettings && r.globalConfigSettings.length > 0
          )
        )
      : "";
  };

  const createImportResponseText = (
    errors: string,
    configParams: string,
    errorSectionMsg: string,
    configSectionMsg: string
  ) => {
    let importResponse = "";
    if (errors.length) {
      const errorText = `#################################################\n\n${errorSectionMsg}\n\n${errors}`;
      importResponse = importResponse.concat(errorText + "\n\n");
    }
    if (configParams.length) {
      const configText = `#################################################\n\n${configSectionMsg}\n${t(
        "MIGRATION_IMPORT_HEADER_SUCCESS_INFO"
      )}\n\n${configParams}`;
      importResponse = importResponse.concat(configText + "\n\n");
    }

    return importResponse;
  };

  const buildErrorMessage = (
    errorTitle: string,
    errorText: string,
    filename: string
  ): React.ReactNode => (
    <div>
      <p className="error-text mt-1">{errorTitle}</p>
      <button
        type="button"
        className="btn btn-link"
        onClick={() => createDownloadableFile(errorText, filename)}
      >
        {t("CSV_ERROR_DOWNLOAD")}
      </button>
    </div>
  );

  return {
    getDeviceImportResponse,
    createDeviceImportErrorText: createDeviceImportError,
    handleDeviceImportResponse,
    handleSiteTopologyResponse,
    handleGlobalConfigResponse,
    handleSiteConfigResponse,
    handleGroupConfigResponse,
    createImportResponseText,
    buildErrorMessage,
    parseSiteTopologyError
  };
};

const Migration: React.FC = () => {
  const { t } = useTranslation();
  const tenantId = useTenantId();

  const { data: devicesData, fetching } = useDeviceCountForTenantQuery({
    variables: {
      tenantId
    },
    pause: !tenantId
  });
  // import should be disabled if account is not empty (has devices) or another import is in progress
  const hasDevices = (devicesData?.deviceCount ?? 0) > 0;

  const prepareFileForImport = async (file: File) => {
    const base64 = (await convertToBase64(file)) as string;
    return base64.split(",").pop() as string;
  };

  const getCSVRules = () => (
    <div className="mb-3">
      <p>{t("MIGRATION_IMPORT_TEXT_1")}</p>
      <p>
        {t("MIGRATION_IMPORT_TEXT_2")}
        <HelpLink location={DOCS.IMPORT_MIGRATION}>
          {t("MIGRATION_IMPORT_TEXT_3")}
        </HelpLink>
        {t("MIGRATION_IMPORT_TEXT_4")}
      </p>
    </div>
  );

  return (
    <Import
      importType="MIGRATION"
      fileTypesAccepted={[FILE_EXTENSIONS.ZIP]}
      validationSchema={migrationFileSchema}
      prepareFileForImport={prepareFileForImport}
      importRules={getCSVRules()}
      loading={fetching}
      disabled={hasDevices}
      disabledReason={
        hasDevices ? t("IMPORT_UNAVAILABLE_ACCOUNT_NOT_EMPTY") : undefined
      }
    />
  );
};

export default Migration;
